// createIDBPersister.js
import { get, set, del, createStore } from 'idb-keyval'

// Timeout wrapper to prevent IndexedDB operations from hanging indefinitely
const withTimeout = (promise, timeoutMs = 3000, operationName = 'operation') => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => {
        reject(new Error(`${operationName} timeout`))
      }, timeoutMs)
    )
  ])
}

// Retry with exponential backoff
const withRetry = async (fn, maxAttempts = 3, baseDelay = 500, operationName = 'operation') => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < maxAttempts) {
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.warn(`[Persister] ${operationName} failed (attempt ${attempt}/${maxAttempts}), retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

// Force delete entire database as last resort
const forceDeleteDatabase = async (dbName) => {
  return new Promise((resolve, reject) => {
    const deleteRequest = indexedDB.deleteDatabase(dbName);

    deleteRequest.onsuccess = () => {
      console.log(`[Persister] Successfully deleted database: ${dbName}`);
      resolve();
    };

    deleteRequest.onerror = () => {
      console.error(`[Persister] Failed to delete database: ${dbName}`, deleteRequest.error);
      reject(deleteRequest.error);
    };

    deleteRequest.onblocked = () => {
      console.warn(`[Persister] Database deletion blocked for: ${dbName}, resolving anyway`);
      resolve(); // Resolve anyway to not block app operation
    };
  });
}

export function createIDBPersister({
  dbName = 'reactQueryDB',
  storeName = 'reactQueryStore',
  key = 'reactQuery',
  serialize = JSON.stringify,
  deserialize = JSON.parse,
  throttleTime = 2000, // Debounce persist operations by 2 seconds
} = {}) {
  let store;

  try {
    store = createStore(dbName, storeName)
  } catch (error) {
    console.error('[Persister] Failed to create IndexedDB store:', error)
    // Return a no-op persister if IndexedDB is unavailable
    return {
      persistClient: async () => {},
      restoreClient: async () => undefined,
      removeClient: async () => {},
    }
  }

  // Debounce state to prevent rapid-fire persist operations
  let pendingClient = null;
  let persistTimeout = null;
  let isPersisting = false;

  const doPersist = async (client) => {
    isPersisting = true;
    try {
      const serializedClient = serialize(client)
      await withTimeout(
        set(key, serializedClient, store),
        10000, // Increased timeout from 5s to 10s for large caches
        'IndexedDB set operation'
      )
    } catch (error) {
      // If quota exceeded or other storage error, try to clear and continue
      if (error.name === 'QuotaExceededError' || error.message?.includes('timeout')) {
        console.warn('[Persister] Storage error detected, attempting to clear cache:', error.message);

        try {
          // Use retry logic with longer timeout for delete operations
          await withRetry(
            () => withTimeout(del(key, store), 5000, 'IndexedDB delete after error'),
            2, // Reduced from 3 retries to 2
            1000, // Increased base delay from 500ms to 1000ms
            'Delete cache after storage error'
          );
        } catch (deleteError) {
          // If delete fails after retries, try nuclear option
          console.error('[Persister] Failed to clear cache after retries, attempting database deletion:', deleteError);

          try {
            await withTimeout(forceDeleteDatabase(dbName), 3000, 'Force delete database');
          } catch (nukeError) {
            // Ultimate fallback - just log and continue
            console.error('[Persister] All cache clearing attempts failed, continuing without persistence:', nukeError);
          }
        }
      }
    } finally {
      isPersisting = false;
      // If another persist was requested while we were persisting, handle it
      if (pendingClient) {
        const nextClient = pendingClient;
        pendingClient = null;
        // Schedule next persist after throttle time
        persistTimeout = setTimeout(() => {
          persistTimeout = null;
          doPersist(nextClient);
        }, throttleTime);
      }
    }
  };

  return {
    persistClient: async (client) => {
      // If already persisting, just queue the latest client
      if (isPersisting) {
        pendingClient = client;
        return;
      }

      // If there's a pending timeout, cancel it and update the pending client
      if (persistTimeout) {
        clearTimeout(persistTimeout);
        pendingClient = client;
        persistTimeout = setTimeout(() => {
          persistTimeout = null;
          const clientToPersist = pendingClient;
          pendingClient = null;
          if (clientToPersist) {
            doPersist(clientToPersist);
          }
        }, throttleTime);
        return;
      }

      // No pending operations - persist immediately, then throttle subsequent calls
      await doPersist(client);
    },
    restoreClient: async () => {
      try {
        // Wrap the get operation with timeout to prevent indefinite hanging
        const serializedClient = await withTimeout(
          get(key, store),
          3000,
          'IndexedDB get operation'
        )

        if (!serializedClient) {
          return undefined
        }

        const deserialized = deserialize(serializedClient)
        return deserialized
      } catch (error) {
        console.warn('[Persister] Failed to restore cache, attempting cleanup:', error.message);

        // If timeout or deserialization fails, the cache is corrupted - clear it
        if (error.message?.includes('timeout')) {
          // Force delete the entire database if it's stuck/corrupted
          try {
            await withTimeout(forceDeleteDatabase(dbName), 3000, 'Force delete corrupted database');
          } catch (deleteError) {
            console.error('[Persister] Failed to delete corrupted database:', deleteError);
          }
        } else {
          // Try to clear just the key if it wasn't a timeout
          try {
            await withRetry(
              () => withTimeout(del(key, store), 5000, 'Delete corrupted key'),
              2,
              500,
              'Clear corrupted cache key'
            );
          } catch (deleteError) {
            // Silently continue - app will work without cache
            console.warn('[Persister] Could not clear corrupted key, continuing without cache');
          }
        }

        // Return undefined to start fresh
        return undefined
      }
    },
    removeClient: async () => {
      try {
        // Use retry logic with increased timeout
        await withRetry(
          () => withTimeout(del(key, store), 5000, 'IndexedDB remove operation'),
          3,
          500,
          'Remove client cache'
        );
      } catch (error) {
        console.warn('[Persister] Failed to remove client cache after retries:', error.message);

        // Try nuclear option as last resort
        try {
          await withTimeout(forceDeleteDatabase(dbName), 3000, 'Force delete database on remove');
        } catch (nukeError) {
          // Ultimate fallback - just log and continue
          // The app can still function without cache persistence
          console.error('[Persister] All removal attempts failed, app will continue without cache persistence:', nukeError);
        }
      }
    },
  }
}
